import { isSearchTextEmptyOrUndefined } from '#PRIVATE_SelectCommon';
import type { ComponentProps } from 'preact';
import { Ref, useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { equals } from 'ramda';
import { CurrentKeyDetail, DataState, SelectionDetail } from '../UNSAFE_Collection';
import { Item } from '../utils/UNSAFE_dataProvider';
import { ItemTextType } from '../utils/UNSAFE_selectUtils';
import type { SelectedValuesCount } from './SelectedValuesCount';
import { useDisplayValues } from './useDisplayValues';
import type { UseSelectMultipleProps, useSelectMultiple } from './useSelectMultiple';
import { useSelectedOnlyData } from './useSelectedOnlyData';

type UseSelectMultipleReturn = ReturnType<typeof useSelectMultiple>;
type PickedPropsFromUseSelectMultiple = Pick<
  UseSelectMultipleReturn,
  | 'isDropdownOpen'
  | 'isDropdownSelectedOnlyView'
  | 'setDropdownOpen'
  | 'setUserInput'
  | 'stopFiltering'
  | 'userInput'
>;
type SelectedValuesCountOnToggle = NonNullable<
  ComponentProps<typeof SelectedValuesCount>['onToggle']
>;

export type UseMobileDropdownProps<K, D> = PickedPropsFromUseSelectMultiple & {
  data: UseSelectMultipleProps<K, D>['data'];
  inputRef: Ref<HTMLElement>;
  itemText: ItemTextType<K, D>;
  textTagListRef: UseSelectMultipleReturn['textTagListProps']['textTagListRef'];
  valueItems: UseSelectMultipleProps<K, D>['valueItems'];
  onCommit: UseSelectMultipleProps<K, D>['onCommit'];
  onLoadRange: UseSelectMultipleProps<K, D>['onLoadRange'];
};

// use const for default valueItems so that it doesn't create a new empty array on every render
const DEFAULT_VALUE_ITEMS = [] as UseMobileDropdownProps<
  string | number,
  Record<string, any>
>['valueItems'];

export function useMobileDropdown<K extends string | number, D>({
  data,
  inputRef,
  isDropdownOpen,
  isDropdownSelectedOnlyView,
  itemText,
  textTagListRef,
  userInput,
  valueItems = DEFAULT_VALUE_ITEMS as NonNullable<UseMobileDropdownProps<K, D>['valueItems']>,
  onCommit,
  onLoadRange,
  setDropdownOpen,
  setUserInput,
  stopFiltering
}: UseMobileDropdownProps<K, D>) {
  const [currentRow, setCurrentRow] = useState<{ rowKey?: K }>({
    rowKey: undefined
  });
  const [dropdownSelectedKeys, setDropdownSelectedKeys] = useState(keysFromValueItems(valueItems));
  const [isInternalSelectedOnlyView, setIsInternalSelectedOnlyView] = useState(
    isDropdownSelectedOnlyView
  );
  const dataCache = useRef(new Map<K, Item<K, D>>());
  const currentValueItems = useMemo(() => {
    const items: Item<K, D>[] = [];
    dropdownSelectedKeys.forEach((key) => {
      // Initially check if they are in valueItems
      const item = valueItems.find((item) => item.key === key);
      if (item) {
        items.push(item);
        return;
      }

      // otherwise check if it is available in the dataCache
      if (dataCache.current.has(key)) {
        items.push(dataCache.current.get(key)!);
      }
    });
    return items;
  }, [dropdownSelectedKeys, valueItems]);
  const { textTags, displayValue } = useDisplayValues(itemText, currentValueItems);

  // Create a ref for the input in the dropdown
  const dropdownInputRef = useRef<HTMLInputElement>(null);

  // show only the selected values in the dropdown if isDropdownSelectedOnlyView is true,
  // otherwise show the full data set
  // TODO: We may need to revisit this for hierarchical data because the selected valueItems
  // won't have any information about ancestor group nodes, and we should probably show selected
  // items in context within the hierarchy.  Also, the order of the items in the selected-only
  // view right now follows the order in which they were selected, not the order in which they
  // would appear in the full data set.  This may be more of a problem for hierarchical data
  // if items have to appear within the correct group ancestors.  We may need to move
  // construction of the selected-only DataState out of the component to the consuming code,
  // and take a callback that we can call as a notification that the app should provide this
  // special DataState to us (kind of like onLoadRange).  Note that we may also need an
  // enhancement on the DataProvider API to be able to specify a filterCriterion to filter by
  // the selected keys through a fetchFirst call, because a regular fetchByKeys call would
  // still return results in the order in which the keys were specified and would not return
  // data for the ancestor group nodes.
  const { data: selectedOnlyData, onLoadRange: selectedOnlyOnLoadRange } = useSelectedOnlyData<
    K,
    D
  >({
    isDropdownSelectedOnlyView: isInternalSelectedOnlyView,
    valueItems: currentValueItems
  });

  const onCurrentKeyChange = useCallback(
    (detail: CurrentKeyDetail<K>) => {
      setCurrentRow({ rowKey: detail.value });
    },
    [setCurrentRow]
  );

  const onSelectionChange = useCallback(
    (detail: SelectionDetail<K>) => {
      const newKeys = new Set(detail.value.keys?.values());
      // update the dropdownSelectedKeys so that the selection visual state of the collection
      // updates immediately while waiting for new valueItems to be set
      setDropdownSelectedKeys(newKeys);

      // clear the filter text typed by the user, but do NOT clear the actual filter criterion
      // or highlighting in the list
      setUserInput('');

      // update the data cache on each selection if data is present
      if (data) {
        updateCache(dataCache.current, data, newKeys);
      }
    },
    [data, setDropdownSelectedKeys, setUserInput]
  );

  const onSelectedValuesCountToggle = useCallback<SelectedValuesCountOnToggle>(
    ({ value = false }) => {
      setIsInternalSelectedOnlyView((currentIsInternalSelectedOnlyView) => {
        // if the selected only toggle is enabled, we need to stop user filtering
        if (currentIsInternalSelectedOnlyView !== value) {
          stopFiltering();
        }
        return value;
      });
    },
    [setIsInternalSelectedOnlyView, stopFiltering]
  );

  const closeDropdown = useCallback(() => {
    // reset dropdownSelectedKeys as you close the dropdown so that
    // the next time the dropdown is opened, it will have the actual component's valueItems
    setDropdownSelectedKeys(new Set(valueItems.map((v) => v.key)));
    // Empty the cached map
    dataCache.current.clear();
    setDropdownOpen(false);
    // stop filtering before closing the dropdown
    stopFiltering();
    // revert the focus back to the main field
    inputRef.current?.focus();
  }, [inputRef, setDropdownOpen, stopFiltering, valueItems]);

  const onApply = useCallback(() => {
    // Commit the current value items and close the dropdown
    onCommit({
      previousValue: keysFromValueItems(valueItems),
      value: dropdownSelectedKeys
    });
    closeDropdown();
  }, [dropdownSelectedKeys, valueItems, closeDropdown, onCommit]);

  const onBackButtonClick = useCallback(() => {
    closeDropdown();
  }, [closeDropdown]);

  const onClearButtonClick = useCallback(() => {
    setDropdownSelectedKeys(new Set());
    stopFiltering();
  }, [stopFiltering]);

  const onTextTagRemove = useCallback(
    (keys: K[]) => {
      const newSelectedKeys = new Set(dropdownSelectedKeys);
      keys.forEach((key) => newSelectedKeys.delete(key));
      setDropdownSelectedKeys(newSelectedKeys);
    },
    [dropdownSelectedKeys]
  );

  const onTextTagExitNavigation = useCallback(() => {
    // Focus the input on the next tick. Doing it in the same event cycle will
    // trigger the key press on the input element which makes the caret move by
    // one character.
    setTimeout(() => dropdownInputRef.current?.focus(), 0);
  }, []);

  const wasDropdownOpen = useRef(isDropdownOpen);
  useEffect(() => {
    // if the dropdown is open, ignore any valueItems update
    if (isDropdownOpen) {
      // if it was open before, then this is a valueItems change, so no action
      if (wasDropdownOpen.current) return;

      // otherwise this dropdown is opened now, so set the focus on the input field
      setTimeout(() => dropdownInputRef.current?.focus(), 0);
      wasDropdownOpen.current = true;
      return;
    }
    wasDropdownOpen.current = false;
  }, [isDropdownOpen]);

  const [prevValueItems, setPrevValueItems] = useState(valueItems);
  if (prevValueItems !== valueItems) {
    // if dropdown is closed, keep the valueItems in sync with the dropdown selected keys
    if (!isDropdownOpen) {
      setDropdownSelectedKeys((prevDropdownSelectedKeys) => {
        const nextDropdownSelectedKeys = keysFromValueItems(valueItems);
        // since valueItems can be a different array with the same values and keysFromValueItems
        // will create a new set every time, compare the value and update the set only if the
        // values have changed
        return equals(prevDropdownSelectedKeys, nextDropdownSelectedKeys)
          ? prevDropdownSelectedKeys
          : nextDropdownSelectedKeys;
      });
    }
    setPrevValueItems(valueItems);
  }

  return {
    collectionProps: {
      currentKey: currentRow.rowKey,
      data: isInternalSelectedOnlyView ? selectedOnlyData : data,
      selectedKeys: dropdownSelectedKeys,
      onCurrentKeyChange,
      onLoadRange: isInternalSelectedOnlyView ? selectedOnlyOnLoadRange : onLoadRange,
      onSelectionChange
    },
    footerProps: {
      onApply
    },
    selectMultipleFieldInputProps: {
      displayValue,
      hasEmptyLabel: false,
      hasInsideLabel: true,
      inputRef: dropdownInputRef,
      isTextTagListShown: textTags.length > 0,
      isUserFiltering: true,
      removeIcon: 'always' as const,
      userInput,
      textTagListData: textTags,
      textTagListRef: textTagListRef,
      onExitNavigation: onTextTagExitNavigation,
      onRemove: onTextTagRemove
    },
    selectMultipleFieldProps: {
      hasInsideLabel: true,
      isBackButtonShown: true,
      isClearButtonShown: !isSearchTextEmptyOrUndefined(userInput) || textTags.length !== 0,
      isDropdownArrowShown: false,
      isDropdownSelectedOnlyView: isInternalSelectedOnlyView,
      isSelectedValuesCountShown: dropdownSelectedKeys.size > 0 || isInternalSelectedOnlyView,
      selectedValuesCount: dropdownSelectedKeys.size,
      styleVariant: 'embedded' as const,
      onBackButtonClick,
      onClearButtonClick
    },
    selectedValuesCountProps: {
      onSelectedValuesCountToggle
    },
    isDropdownSelectedOnlyView: isInternalSelectedOnlyView
  };
}

/**
 * A function to cache the data for the current set of keys from the current data state
 * and current set of keys.
 *
 * The idea is that, the data state might not contain all the selected keys. However, as
 * a key is selected, if we update the cache, it will always contain the data for the selected
 * keys. Reason is that, when a key is selected, at that moment it will be in the current data
 * state. We will fetch that information and store it. While the current data state might not
 * contain information about all the selected keys, it will always have the information about
 * current selected key.
 *
 * @param cache The data cache
 * @param data The current data state
 * @param keys The current set of keys
 */
const updateCache = <K, D>(cache: Map<K, Item<K, D>>, data: DataState<K, D>, keys: Set<K>) =>
  // iterate through the data and if a selected key's match is found, update the cache
  data.data.forEach((item) => {
    if (!keys.has(item.metadata.key)) return;

    cache.set(item.metadata.key, {
      data: item.data,
      key: item.metadata.key,
      metadata: item.metadata
    });
  });

/**
 * Extract keys from a value items array.
 *
 * @param valueItems The value items from which the keys are to be extracted
 * @returns The set of keys
 */
const keysFromValueItems = <K, D>(valueItems: Item<K, D>[]) =>
  new Set(valueItems.map((i) => i.key));
